import { type ReactNode, useCallback, useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	injectOneMindLauncher,
	removeOneMindLauncher,
	subscribeToOneMindMessages,
} from './one-mind-launcher';

/** Analytics source for every 1mind event, so they can be filtered as a group. */
export const ONE_MIND_ANALYTICS_SOURCE = 'oneMindWidget';

export interface OneMindProviderProps {
	/**
	 * The 1mind vendor deployment id for the persona to load, e.g.
	 * `deployment-rprgpn5ltf`. See `ONE_MIND_DEPLOYMENTS`.
	 */
	deploymentId: string;
	/**
	 * The experiment cell this widget was rendered for (e.g. `ai_guide`).
	 * Emitted on every event so engagement can be compared across cells without
	 * having to join back to the Statsig exposure table.
	 */
	variantName?: string;
	children?: ReactNode;
}

/**
 * Mounts the 1mind launcher for a single persona and renders its children
 * unchanged (a pass-through provider, matching the shape of
 * `ChatWidgetProvider` / `IntercomProvider` so it can be swapped in at the same
 * page-level branch points).
 *
 * The launcher script is injected client-side on mount and torn down on
 * unmount. The vendor renders its own floating widget UI, so this provider
 * renders no chrome of its own.
 *
 * Analytics (GWP-12151): emits `ui` events so the whole funnel lands in
 * `analyticsplat.event_ui` and a Statsig CTR-style metric can be defined as
 * `engaged / loaded` per experiment cell:
 *  - `oneMindWidget loaded`  — vendor script resolved, widget should be visible
 *  - `oneMindWidget failed`  — vendor script blocked or errored (guardrail)
 *  - `oneMindWidget engaged` — visitor interacted with the widget
 */
export const OneMindProvider = ({ deploymentId, variantName, children }: OneMindProviderProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Keep the latest factory in a ref so the injection effect depends only on
	// `deploymentId` — otherwise a new `createAnalyticsEvent` identity would tear
	// down and re-inject the vendor script on every render.
	const createAnalyticsEventRef = useRef(createAnalyticsEvent);
	createAnalyticsEventRef.current = createAnalyticsEvent;

	const fireOneMindEvent = useCallback(
		(action: 'loaded' | 'failed' | 'engaged', attributes: Record<string, string> = {}) => {
			createAnalyticsEventRef
				.current({
					eventType: 'ui',
					action,
					actionSubject: ONE_MIND_ANALYTICS_SOURCE,
					actionSubjectId: deploymentId,
					source: ONE_MIND_ANALYTICS_SOURCE,
					attributes: {
						deploymentId,
						...(variantName && { variantName }),
						...attributes,
					},
				})
				.fire();
		},
		[deploymentId, variantName],
	);

	useEffect(() => {
		injectOneMindLauncher(deploymentId, {
			onLoad: () => fireOneMindEvent('loaded'),
			onError: () => fireOneMindEvent('failed'),
		});

		return () => removeOneMindLauncher(deploymentId);
	}, [deploymentId, fireOneMindEvent]);

	useEffect(
		() =>
			subscribeToOneMindMessages((vendorEventName) =>
				fireOneMindEvent('engaged', { vendorEventName }),
			),
		[fireOneMindEvent],
	);

	return <>{children}</>;
};

export default OneMindProvider;
